import React from 'react';

/**
 * MarkerLegend (Lane 135) — a visible, always-discoverable decoder for the
 * inline row markers (the orange asterisk and dagger). The markers themselves
 * (Lane 91/94/96 AnomalyExclusionTag, Lane 121 MetricFlagTag, the IQR outlier
 * asterisk) already carry hover tooltips, but Ryan's staging QA (2026-07-15
 * item 4) found nobody discovers a tooltip on a bare glyph — this legend puts
 * the meaning ON the page, under the table, only for the marker kinds the
 * current rows actually render.
 *
 * The copy per kind is derived from the existing Lane 121/122 tooltip texts —
 * it RESTATES what each marker means and never characterizes the data. A kind
 * that is not present in the current rows renders no legend line, so a clean
 * table (or a guard-OFF response, where the keys are absent) shows nothing and
 * stays pixel-identical to today.
 *
 * Linear rebuild Stage 2 (Lane 219, cross-page chrome): renders only under the
 * tables inside Linear page scopes (SupplierDetail `.sd` + Analytics `.an`), so
 * the hairline top border, muted text, and amber glyph read the Linear tokens
 * (`var(--hair)` / `var(--text-3)` / `var(--warn)`) directly — was the Meridian
 * `colors.*`, matching the Lane 215 dossier-child convention.
 */

export const LEGEND_COPY = {
  outlier: {
    glyph: '*',
    text: 'Unusually large move, kept in the math. Verify the source POs.',
  },
  exclusion: {
    glyph: '†',
    text: 'A mis-recorded order was excluded. Hover the marker for details.',
  },
  verify: {
    glyph: '†',
    text: "Real but outsized: beyond ±100% or a year's spend. Hover the marker for why.",
  },
  coverage: {
    glyph: '†',
    text: "Partial read: under 40% of that supplier's annual spend.",
  },
};

const KIND_ORDER = ['outlier', 'exclusion', 'verify', 'coverage'];

/**
 * Props:
 *   kinds — array of kind keys present in the table's current rows
 *           (subset of: 'outlier' | 'exclusion' | 'verify' | 'coverage').
 * Unknown kinds are ignored; an empty/absent list renders nothing.
 */
function MarkerLegend({ kinds }) {
  const present = KIND_ORDER.filter((k) => (kinds || []).includes(k));
  if (present.length === 0) return null;
  return (
    <div
      style={{
        marginTop: '8px',
        padding: '8px 12px',
        borderTop: '1px solid var(--hair)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--text-3)',
        lineHeight: '1.6',
      }}
    >
      {present.map((k) => (
        <div key={k}>
          <span style={{ color: 'var(--warn)', fontWeight: 'var(--font-weight-semibold)', marginRight: '6px' }}>
            {LEGEND_COPY[k].glyph}
          </span>
          {LEGEND_COPY[k].text}
        </div>
      ))}
    </div>
  );
}

export default MarkerLegend;
